import ExpandableFilterPanel from 'components/molecules/expandable-filter-panel';
import { useLinkCallReadingsToolFilters } from 'infrastructure/hooks/link-call-readings-tool/use-link-call-readings-tool-filter';
import { useTranslation } from 'react-i18next';
import BaseInput from 'components/atoms/base-input';
import BaseRangeDatepicker from 'components/atoms/base-range-datepicker';
import { endOfDate, startOfDate } from 'infrastructure/functions';

import s from './styles.module.scss';

import type { ReactNode, FC } from 'react';
import type {
  RangePickerValue,
  TOnCalendarChange,
} from 'components/atoms/base-range-datepicker';
import type { ILinkCallReadingsToolFilters } from 'infrastructure/hooks/link-call-readings-tool/types';

const ItemWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  return <div className={s['filter-item-wrapper']}>{children}</div>;
};

const LinkCallReadingsFilters: FC = () => {
  const { filter, count, resetFilter, updateFilter } =
    useLinkCallReadingsToolFilters();
  const { t } = useTranslation();

  const appendFiltersAndResetPage = (
    newValue: Partial<ILinkCallReadingsToolFilters>,
  ) => {
    updateFilter({ page: undefined, ...newValue });
  };

  const changeTimeRange: TOnCalendarChange = (dates, _, info, preset) => {
    const [start, end] = dates;

    if (!start && !end) {
      appendFiltersAndResetPage({
        startTime: undefined,
        endTime: undefined,
      });
      return;
    }

    if (preset && start && end) {
      appendFiltersAndResetPage({
        startTime: startOfDate(start).getTime(),
        endTime: endOfDate(end).getTime(),
      });
      return;
    }

    if (start && info.range === 'start') {
      appendFiltersAndResetPage({
        startTime: startOfDate(start).getTime(),
      });
    }
    if (end && info.range === 'end') {
      appendFiltersAndResetPage({
        endTime: endOfDate(end).getTime(),
      });
    }
  };

  const selectedRange: RangePickerValue = [
    filter.startTime ? new Date(filter.startTime) : null,
    filter.endTime ? new Date(filter.endTime) : null,
  ];

  return (
    <div className={s.wrapper}>
      <ExpandableFilterPanel filtersCount={count} onReset={resetFilter}>
        <div className={s.body}>
          <ItemWrapper>
            <label htmlFor="search-by-date-range">
              {t('labels.disconnectTimeRange')}
            </label>
            <BaseRangeDatepicker
              allowClear={false}
              withPresets
              onCalendarChange={changeTimeRange}
              value={selectedRange}
              id="search-by-date-range"
            />
          </ItemWrapper>
          <ItemWrapper>
            <label htmlFor="search-by-agent-email">{t('labels.agent')}</label>
            <BaseInput
              value={filter.agent}
              onChange={(value) => {
                appendFiltersAndResetPage({ agent: value });
              }}
              label={t('labels.agent')}
              id="search-by-agent-email"
              placeholder={t('placeholders.searchByAgentEmail')}
              allowClear
            />
          </ItemWrapper>
          <ItemWrapper>
            <label htmlFor="search-by-call-id">{t('labels.callId')}</label>
            <BaseInput
              value={filter.callId}
              onChange={(value) => {
                appendFiltersAndResetPage({ callId: value });
              }}
              label={t('labels.callId')}
              id="search-by-call-id"
              placeholder={t('placeholders.searchByCallId')}
              allowClear
            />
          </ItemWrapper>
          <ItemWrapper>
            <label htmlFor="search-by-source-phone">
              {t('labels.sourcePhone')}
            </label>
            <BaseInput
              value={filter.sourcePhone}
              onChange={(value) => {
                appendFiltersAndResetPage({ sourcePhone: value });
              }}
              label={t('labels.sourcePhone')}
              id="search-by-source-phone"
              placeholder={t('placeholders.searchByPhone')}
              allowClear
            />
          </ItemWrapper>
        </div>
      </ExpandableFilterPanel>
    </div>
  );
};

export default LinkCallReadingsFilters;
